<template>
  <div class="fadein">
    <div class="ticket_contact_list_header">
      <div class="d_flex_btwn">
        <div class="order_filter_content ">
          <div class="d_flex_start">
            <input type="text" class="form_global width_260p mr_5" placeholder="Search"
                   v-on:keyup.enter="searchRecord()" v-model="queryParams.name">
            <button class="btn btn_black mr_5 width_100p pt_10 pb_10" @click="queryParams.page = 1 ; searchRecord()">
              Search
            </button>
            <button class="btn btn_black width_100p pt_10 pb_10 mr_5" @click="resetAll()">Reset</button>
            <select
                class="form_global width_150p"
                v-model="filter.per_page"
                @change=" queryParams.page = 1; changeRoute()"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="vlan_ticket_contact_list_page ">
      <div class="vlan_ticket_contact_list_page_wrap ">
        <div class="vlan_ticket_contact_list_inner">
          <div class="mb_20">
            <ticket-table-component
                :columns="columns"
                :rows="records"
                :sortable="sortable"
                :pagination="pagination"
                @sorting="sorting"
                @pageChanged="pageChanged"
            >
              <template v-slot:loggable_type="{ row }">
                {{ reformatText(row.loggable_type) }}
              </template>
              <template v-slot:action="{ row }">
                <a href="javascript:void(0)" class="btn btn_black mb_5" @click="openInvoiceHistoryModal(row)">
                  Details
                </a>
              </template>
            </ticket-table-component>
          </div>
        </div>
      </div>
    </div>

    <BaseModal
        v-if="isShowDetailsModal"
        title="Invoice Payment History"
        :closeModal="closeInvoiceHistoryModal"
        size="modal_1060p"
    >
      <CustomerAuditLogHistory
          :closeModal="closeInvoiceHistoryModal"
          :record="record"
      />
    </BaseModal>

  </div>
</template>

<script>
import TicketTableComponent from "@/components/shared/TicketTableComponent";
import BaseModal from "@/components/BaseModal.vue";
import CustomerAuditLogHistory from "@/views/customers/CustomerAuditLogHistory.vue";

// Hoisted so data(), setQueryData() and resetAll() share one definition of the
// unfiltered state: a param missing from the URL has to fall back to its
// default, never to undefined (axios drops those, and a dropped per_page makes
// the API return an unpaginated payload).
const defaultQueryParams = () => ({
  page: 1,
  search: '',
  search_columns: 'action,actor_type,loggable_type',
  sort: 'created_at',
  sort_order: 'desc',
  per_page: 20
})

const defaultFilter = () => ({
  status: '',
  // Set by the resource picker; missing from data() but read back out of the
  // URL, so they have to be part of the defaults to round-trip.
  loggable_id: '',
  loggable_type: '',
  per_page: 20
})

export default {
  name: 'CustomerAuditLogs',
  components: {CustomerAuditLogHistory, BaseModal, TicketTableComponent},
  data() {
    return {
      lyLoading: false,
      customer_id: this.$route.params.id,
      isShowDetailsModal: false,
      record: null,
      records: [],
      filter: defaultFilter(),
      queryParams: defaultQueryParams(),
      sortable: {
        order: 'created_at',
        sort: 'desc'
      },
      columns: [
        {
          label: "Resource",
          field: "loggable_type",
          slot: true,
        },
        {
          label: "User",
          field: "actor.name",
          sortable: false,
        },
        {
          label: "Event",
          field: "action",
          sortable: true,
        },
        {
          label: "Created At",
          field: "created_at",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          slot: true,
        },
      ],
      resources: [],
      pagination: null,
    }
  },
  watch: {
    $route(to, from) {
      if (to.name === from.name && !window._.isEqual(to, from)) {
        this.loadContactData()
      }
    },
    sort() {
      if (this.sort === '1') {
        this.sorting({column: 'image', order: 'asc'})
      } else if (this.sort === '2') {
        this.sorting({column: 'image', order: 'desc'})
      } else if (this.sort === '3') {
        this.sorting({column: 'updated_at', order: 'desc'})
      }
    }
  },
  created() {
    // Unconditional: partial deep links (?search=x) have to seed state too.
    this.setQueryData(this.getAllQueryParams())
  },
  mounted() {
    if (this.$route.params.id) {
      this.loadContactData()
      this.ticketScrollbar()
    }
  },
  methods: {
    setQueryData(query) {
      this.queryParams = this.withQueryDefaults(defaultQueryParams(), query)
      this.filter = this.withQueryDefaults(defaultFilter(), query)

      this.sortable = {
        order: this.queryParams.sort,
        sort: this.queryParams.sort_order
      }
    },
    resetAll() {
      this.queryParams = defaultQueryParams()
      this.filter = defaultFilter()

      this.sortable = {
        order: this.queryParams.sort,
        sort: this.queryParams.sort_order
      }

      this.changeRoute();
      this.loadContactData();
    },
    searchQuery() {
      // A new search/filter changes the result set, so the old page number
      // no longer means anything — start from the first page.
      this.queryParams.page = 1;
      this.changeRoute();
    },
    sorting({column, order}) {
      this.queryParams.sort = column;
      this.queryParams.sort_order = order;
      this.queryParams.page = 1;
      this.changeRoute()
    },
    pageChanged(page) {
      this.queryParams.page = page;
      this.changeRoute()
    },
    changeRoute() {
      this.$router.push({name: this.$route.name, query: {...this.queryParams, ...this.filter}})
    },
    loadContactData() {
      this.lyLoading = true;
      this.axios.get('/customer-audit-logs/' + this.customer_id, {
        params: {...this.queryParams, ...this.filter}
      })
          .then(({data}) => {
            this.records = data.data
            this.pagination = data.meta
          })
          .catch(() => {
          })
          .finally(() => {
            this.ticketScrollbar()
            this.lyLoading = false;
          })
    },
    openInvoiceHistoryModal(record) {
      this.record = record
      this.isShowDetailsModal = true
    },
    closeInvoiceHistoryModal() {
      this.record = null
      this.isShowDetailsModal = false
    },
    filterByResource(resourceId) {
      this.queryParams.page = 1;

      if (resourceId) {
        const selectedResource = this.resources.find(
            (resource) => resource.id === resourceId
        );

        this.filter.loggable_id = selectedResource.loggable_id;
        this.filter.loggable_type = selectedResource.loggable_type;
      } else {
        this.filter.loggable_id = '';
        this.filter.loggable_type = '';
      }
      this.changeRoute()
    },
    filterByYearMonth() {
      this.queryParams.page = 1;
      this.changeRoute()
    },
    reformatText(text) {
      return text.replace(/App\\Models\\(Cloud\\)?/, "");
    },
    getResourceList() {
      this.axios.get(`/customer-audit-log-resources/${this.customer_id}`).then(({data}) => {
        this.resources = data
      })
    },
    searchRecord() {
      // A new search/filter changes the result set, so the old page number
      // no longer means anything — start from the first page.
      this.queryParams.page = 1;
      this.changeRoute();
    },
  }
}
</script>
<style scoped>

</style>
